/**
 * `diagram/elkLayout.ts` — professional auto-layout via the Eclipse Layout Kernel (elkjs)
 * (professional-polish P1, 2026-07-08).
 *
 * Our hand-rolled longest-path layout (`layout.ts`) is deterministic and dependency-free but lays
 * a pipeline out as a long thin strip with no crossing minimization. ELK's `layered` algorithm
 * gives the compact, balanced, crossing-minimized, subgraph-clustered layout you'd get from
 * Lucid/Visio. It is **async**, so the canvas renders immediately with the sync `computeLayout`
 * (also the no-ResizeObserver SSR path) and swaps in ELK positions when they resolve.
 *
 * Only runs for a diagram with NO hand-tuned positions (a pinned diagram keeps the user's layout).
 * Subgraphs become ELK compound nodes (native nesting → tight, correct group boxes). Uses the
 * main-thread bundled build (`elk.bundled.js`) — no web worker, so no vite worker/CSP friction.
 */

import type { DiagramModel, NodeShape, Point } from './model';

/**
 * elkjs is large — load it lazily as its own chunk (dynamic import), so it isn't bundled into the
 * main app chunk (which OOM'd the CI vite build) and only downloads when a diagram first lays out.
 * The instance is created once and reused.
 */
type ElkInstance = { layout: (graph: unknown) => Promise<unknown> };
let elkPromise: Promise<ElkInstance> | null = null;
function getElk(): Promise<ElkInstance> {
  if (elkPromise === null) {
    elkPromise = import('elkjs/lib/elk.bundled.js').then((m) => {
      const Ctor = (m.default ?? m) as new () => ElkInstance;
      return new Ctor();
    });
  }
  return elkPromise;
}

/** mermaid flow direction → ELK direction. */
function elkDirection(direction: string): 'RIGHT' | 'LEFT' | 'UP' | 'DOWN' {
  switch (direction) {
    case 'LR':
      return 'RIGHT';
    case 'RL':
      return 'LEFT';
    case 'BT':
      return 'UP';
    default:
      return 'DOWN'; // TB / TD
  }
}

interface ElkNode {
  id: string;
  width?: number;
  height?: number;
  children?: ElkNode[];
  layoutOptions?: Record<string, string>;
  x?: number;
  y?: number;
}

/**
 * Compute absolute top-left positions for every auto node via ELK layered layout. `sizeOf` gives
 * each node's `[w, h]` (same estimate the canvas + overlap gate use). Returns null on any failure
 * (caller falls back to the sync layout).
 */
export async function elkLayout(
  model: DiagramModel,
  sizeOf: (id: string, label: string, shape: NodeShape) => Point,
): Promise<Map<string, Point> | null> {
  const ids = [...model.nodes.keys()];
  if (ids.length === 0) return new Map();

  // node id → owning subgraph id (first subgraph that lists it; innermost wins by document order).
  const parentOf = new Map<string, string>();
  for (const sg of model.subgraphs) {
    for (const m of sg.members) {
      if (model.nodes.has(m) && !parentOf.has(m)) parentOf.set(m, sg.id);
    }
  }

  const leaf = (id: string): ElkNode => {
    const n = model.nodes.get(id)!;
    const [w, h] = sizeOf(id, n.label, n.shape);
    return { id, width: w, height: h };
  };

  // Build the compound tree: a node per subgraph (with its members as children) + loose nodes.
  const groupNodes = new Map<string, ElkNode>();
  for (const sg of model.subgraphs) {
    groupNodes.set(sg.id, {
      id: `grp:${sg.id}`,
      children: [],
      // Match the drawn frame's header + inner padding (DiagramCanvas frame chrome) so tiles don't
      // crowd the label or bleed into a neighbouring frame (WS3.3/L6).
      layoutOptions: { 'elk.padding': '[top=42,left=20,bottom=20,right=20]' },
    });
  }
  const rootChildren: ElkNode[] = [];
  const placedGroups = new Set<string>();
  for (const id of ids) {
    const gid = parentOf.get(id);
    if (gid && groupNodes.has(gid)) {
      groupNodes.get(gid)!.children!.push(leaf(id));
      if (!placedGroups.has(gid)) {
        rootChildren.push(groupNodes.get(gid)!);
        placedGroups.add(gid);
      }
    } else {
      rootChildren.push(leaf(id));
    }
  }
  // Drop any empty group that somehow got no members.
  for (const g of groupNodes.values()) if ((g.children?.length ?? 0) === 0) g.children = [];

  // Pass each edge's label to ELK as a sized child of the edge (a "label dummy"). ELK then RESERVES
  // space for it — spreading the layers/nodes so the label lands in open space instead of clipping
  // a tile (WS3.2/L1: the "enqueue/dequeue" overlap). We keep rendering the label at the smoothstep
  // midpoint (FloatingEdge), which now falls inside that reserved gap. Width mirrors the tile
  // estimator's glyph advance; a compact height for one line.
  const edges = model.edges.map((e, i) => {
    const text = (e.label ?? '').trim();
    return {
      id: `e${i}`,
      sources: [e.from],
      targets: [e.to],
      ...(text
        ? { labels: [{ text, width: Math.round(text.length * 13 * 0.6 + 10), height: 16 }] }
        : {}),
    };
  });

  const graph: ElkNode & { edges: typeof edges } = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': elkDirection(model.direction),
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
      // Crossing-minimizing node placement (NETWORK_SIMPLEX) + a thorough sweep → fewer crossings,
      // more balanced columns than the default BRANDES_KOEPF (WS3.3/L5).
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.thoroughness': '10',
      'elk.layered.spacing.nodeNodeBetweenLayers': '64',
      'elk.spacing.nodeNode': '48',
      'elk.spacing.edgeEdge': '14',
      'elk.layered.spacing.edgeNodeBetweenLayers': '28',
      'elk.spacing.edgeNode': '24',
      'elk.spacing.edgeLabel': '6',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    },
    children: rootChildren,
    edges,
  };

  let laid: ElkNode;
  try {
    const elk = await getElk();
    laid = (await elk.layout(graph)) as ElkNode;
  } catch {
    return null; // ELK load or layout failure → caller uses the sync fallback
  }

  // Walk the result, accumulating parent offsets → absolute top-left per leaf node.
  const positions = new Map<string, Point>();
  const walk = (node: ElkNode, ox: number, oy: number): void => {
    for (const child of node.children ?? []) {
      const ax = ox + (child.x ?? 0);
      const ay = oy + (child.y ?? 0);
      if (child.id.startsWith('grp:')) {
        walk(child, ax, ay); // descend into the subgraph; its own box isn't a graph node
      } else if (model.nodes.has(child.id)) {
        positions.set(child.id, [Math.round(ax), Math.round(ay)]);
      }
    }
  };
  walk(laid, 0, 0);
  return positions.size > 0 ? positions : null;
}
